import React, { useState } from 'react';
import { SelectChangeEvent } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { TFunction, useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  CustomSelect,
  DropDownItem,
  Grid,
  Typography,
} from 'web-library';
import { useHistory } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Controller, UseFormSetValue } from 'react-hook-form';
import RouteKey from '../../../routes/key';
import { usePostUnlockAccountForm } from '../logic/usePostUnlockAccountForm';
import { PostUnlockAccountFormData } from '../../../api-refactored/auth/usePostUnlockAccount';
import {typeTheme} from "web-library/theme/helper/tssHelper"
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";
import TextFieldForm from "web-library/stories/atom/TextField/TextFieldForm";

const useStyles = makeStyles()((theme) =>
({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  formElementContainer: {
    marginBottom: theme.spacing(4),
    maxWidth: '50%',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
    },
  },
  apiMessageContainer: {
    color: theme.color.state.stateSuccess,
    marginBottom: theme.spacing(2),
  },
  apiMessage: {
     ...typeTheme(theme, 'helperText'),
  },
  actionButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginRight: 0,
      marginBottom: theme.spacing(2),
    },
  },
}));
const { auth: authRoute } = RouteKey.child;
const onSuccessCallback = (
  setApiMessage: React.Dispatch<React.SetStateAction<string | undefined>>,
  setApiError: React.Dispatch<React.SetStateAction<boolean>>,
  t: TFunction
) => {
  setApiError(false);
  setApiMessage(t('profile->>UnlockAccount.Success'));
};
const onFailureCallback = (
  err: AxiosError,
  setApiMessage: React.Dispatch<React.SetStateAction<string | undefined>>,
  setApiError: React.Dispatch<React.SetStateAction<boolean>>,
  t: TFunction
) => {
  setApiError(true);
  if (err.response?.status) {
    switch (err.response.status) {
      case 404:
        setApiMessage(t('profile->>UnlockAccount.Error.UserNotFound'));
        break;
      default:
        setApiMessage(t('profile->>UnlockAccount.Error.UnknownError'));
    }
  }
};

type CredentialType = 'USERNAME' | 'SINGPASS' | 'MIMS';

const accountTypes: DropDownItem[] = [
  { key: 'USERNAME' as CredentialType, value: 'USERNAME' as CredentialType, children: 'CP Account' },
  { key: 'SINGPASS' as CredentialType, value: 'SINGPASS' as CredentialType, children: 'Singpass' },
  { key: 'MIMS' as CredentialType, value: 'MIMS' as CredentialType, children: 'MIMS' },
];

// const accountTypes: DropDownItem[] = [
//     { key: 'USERNAME', value: 'USERNAME', children: 'CP Account' },
//     { key: 'SINGPASS', value: 'SINGPASS', children: 'Singpass' },
//     { key: 'MIMS', value: 'MIMS', children: 'MIMS' },
// ];
const accountTypeOnChanged = (
  event: SelectChangeEvent<unknown>,
  setAccountType: React.Dispatch<React.SetStateAction<CredentialType>>,
  setValue: UseFormSetValue<PostUnlockAccountFormData>
) => {
  setAccountType(event.target.value as CredentialType);
  setValue('accountType', event.target.value as CredentialType);
};
const UnlockAccountForm = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const history = useHistory();
  const [apiMessage, setApiMessage] = useState<string>();
  const [apiError, setApiError] = useState<boolean>(false);
  const { form, postUnlockAccount, postUnlockAccountMutation } = usePostUnlockAccountForm(
    t,
    () => onSuccessCallback(setApiMessage, setApiError, t),
    (err) => onFailureCallback(err, setApiMessage, setApiError, t)
  );
  const { handleSubmit, formState: { errors }, setValue, control } = form;
  const [selectedAccountType, setSelectedAccountType] = useState<CredentialType>('USERNAME');
  return (
    <Box className={classes.container}>
      <form
        onSubmit={handleSubmit((formData) => {
          postUnlockAccount(formData);
        })}
      >
        <Controller
          name="accountType"
          control={control}
          defaultValue={selectedAccountType}
          render={({field}) => (
            <CustomSelect
            {...field}
              id="select-account-type"
              label={t('profile->>UnlockAccount.AccountType')}
              inputProps={{ 'aria-label': t('profile->>UnlockAccount.AccountType') }}
              placeholder={t('profile->>UnlockAccount.Placeholder.AccountType')}
              error={!!errors.accountType}
              helperText={errors?.accountType?.message?.toString()}
              menuItems={accountTypes}
              defaultValue={selectedAccountType}
              value={selectedAccountType}
              name="accountType"
              className={classes.formElementContainer}
              isRequired
              onChange={(e) => {
                field.onChange(e);
                accountTypeOnChanged(e, setSelectedAccountType, setValue);
              }}
            />
          )}
        />

        <TextFieldForm
          id="input-username"
          label={t(`profile->>UnlockAccount.${selectedAccountType}`)}
          placeholder={t(`profile->>UnlockAccount.Placeholder.${selectedAccountType}`)}
          isRequired
          form={form}
          error={!!errors.username}
          helperText={
            errors?.username?.message
              ? t(`profile->>UnlockAccount.Error.${selectedAccountType}`)
              : ''
          }
          name="username"
          type="text"
          className={classes.formElementContainer}
        />

        <ControlledDatePicker
            label={t('profile->>UnlockAccount.DateOfBirth')}
            placeholder={t('profile->>UnlockAccount.Placeholder.DateOfBirth')}
            id="dateOfBirth"
            isRequired
            inputVariant="filled"
            control={control}
            error={!!errors.dateOfBirth}
            helperText={errors?.dateOfBirth?.message?.toString()}
            name="dateOfBirth"
            selectOnly={false}
            disableFuture
            // value={dateOfBirth}
            onChange={(date) => {
              setValue('dateOfBirth', date || '');
            }}
            className={classes.formElementContainer}
        />
        {apiMessage && (
          <Box className={classes.apiMessageContainer}>
            <Typography
              className={classes.apiMessage}
              variant="body1"
              color={apiError ? 'error' : 'inherit'}
            >
              {apiMessage}
            </Typography>
          </Box>
        )}
        <Grid container direction="row" justify="flex-start">
          <Button
            variant="outlined"
            className={classes.actionButton}
            onClick={() => {
              history.push(`/${authRoute}/`);
            }}
          >
            {t('profile->>UnlockAccount.Back')}
          </Button>
          <ApiButton
            className={classes.actionButton}
            status={postUnlockAccountMutation.status}
            type="submit"
          >
            {t('profile->>UnlockAccount.Submit')}
          </ApiButton>
        </Grid>
      </form>
    </Box>
  );
};

export default UnlockAccountForm;
